/**
 * Coach — §6 & §7. Donne la main sur le moteur d'intelligence à 3 niveaux :
 *
 *   • Niveau 1 (Rules) : « Lancer l'analyse » produit des conseils déterministes
 *     (alternance charge/repos, surcharge, régularité, échéances).
 *   • Niveau 3 (AI Coach) : « Générer le bilan » rédige un bilan hebdomadaire et
 *     une recommandation. L'IA n'invente pas le plan — elle explique les
 *     chiffres validés par les niveaux 1 et 2.
 *
 * La prochaine séance optimale (Q4) est rappelée en tête.
 */
import React, { useEffect, useState } from 'react'
import { Box, Button, Stack, Typography } from '@mui/material'
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded'
import PsychologyRoundedIcon from '@mui/icons-material/PsychologyRounded'

import { performanceApi } from '../api/performanceApi'
import {
  Loading,
  PERF_ACCENT,
  Pill,
  SEVERITY_COLOR,
  SectionTitle,
  cardSx,
} from './shared'

const SOURCE_LABELS = {
  rules: 'Règles',
  progress: 'Analyse',
  ai_coach: 'Coach IA',
}

export default function CoachView() {
  const [next, setNext] = useState(null)
  const [advices, setAdvices] = useState([])
  const [review, setReview] = useState(null)
  const [loading, setLoading] = useState(true)
  const [running, setRunning] = useState(false)
  const [reviewing, setReviewing] = useState(false)

  const load = async () => {
    setLoading(true)
    const [n, a, reviews] = await Promise.all([
      performanceApi.getNextSession(),
      performanceApi.advices.list('?active=1'),
      performanceApi.weeklyReviews.list(),
    ])
    setNext(n)
    setAdvices(a)
    setReview(reviews && reviews.length ? reviews[0] : null)
    setLoading(false)
  }

  useEffect(() => {
    load()
  }, [])

  const runEngine = async () => {
    setRunning(true)
    try {
      await performanceApi.runEngine()
      const a = await performanceApi.advices.list('?active=1')
      setAdvices(a)
    } finally {
      setRunning(false)
    }
  }

  const genReview = async () => {
    setReviewing(true)
    try {
      const r = await performanceApi.generateWeeklyReview({})
      setReview(r)
    } finally {
      setReviewing(false)
    }
  }

  const dismiss = async (id) => {
    await performanceApi.advices.update(id, { is_dismissed: true })
    setAdvices((prev) => prev.filter((a) => a.id !== id))
  }

  if (loading) return <Loading label="Préparation de votre coach…" />

  return (
    <Box>
      {/* Q4 — prochaine séance */}
      <Box sx={{ ...cardSx, mb: 3, borderLeft: `4px solid ${PERF_ACCENT}` }}>
        <Typography sx={{ color: PERF_ACCENT, fontWeight: 700, mb: 0.5 }}>
          Votre prochaine séance
        </Typography>
        {next?.session ? (
          <>
            <Typography sx={{ color: '#fff', fontWeight: 600 }}>
              {next.session.title} · {next.session.type} · {next.session.date}
            </Typography>
            <Typography
              sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 14, mt: 0.5 }}
            >
              {next.explanation}
            </Typography>
          </>
        ) : (
          <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 14 }}>
            {next?.explanation ||
              "Aucune séance planifiée. Générez votre planning d'abord."}
          </Typography>
        )}
      </Box>

      {/* Bilan hebdo (niveau 3) */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 1.5 }}
      >
        <SectionTitle>Bilan hebdomadaire</SectionTitle>
        <Button
          startIcon={<PsychologyRoundedIcon />}
          onClick={genReview}
          disabled={reviewing}
          sx={{
            color: PERF_ACCENT,
            textTransform: 'none',
            fontWeight: 600,
          }}
        >
          {reviewing ? 'Génération…' : 'Générer le bilan'}
        </Button>
      </Stack>
      <Box sx={{ ...cardSx, mb: 3 }}>
        {review ? (
          <>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 1 }}
            >
              <Typography sx={{ color: '#fff', fontWeight: 700 }}>
                Semaine du {review.week_start}
              </Typography>
              <Pill label={SOURCE_LABELS[review.generated_by] || review.generated_by} />
            </Stack>
            <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
              <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 13 }}>
                {review.sessions_completed}/{review.sessions_planned} séances
              </Typography>
              {review.compliance_pct != null && (
                <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 13 }}>
                  Compliance {review.compliance_pct}%
                </Typography>
              )}
              {review.total_load != null && (
                <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 13 }}>
                  Charge {review.total_load}
                </Typography>
              )}
              {review.load_delta_pct != null && (
                <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 13 }}>
                  {review.load_delta_pct > 0 ? '+' : ''}
                  {review.load_delta_pct}% vs S-1
                </Typography>
              )}
            </Stack>
            <Typography sx={{ color: '#fff', fontSize: 14, mb: 1 }}>
              {review.summary}
            </Typography>
            {review.recommendation && (
              <Typography sx={{ color: PERF_ACCENT, fontSize: 14 }}>
                → {review.recommendation}
              </Typography>
            )}
          </>
        ) : (
          <Typography sx={{ color: 'rgba(255,255,255,0.55)' }}>
            Aucun bilan pour le moment. Générez-en un pour obtenir une synthèse
            de votre semaine et une recommandation pour la suivante.
          </Typography>
        )}
      </Box>

      {/* Conseils (niveau 1) */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 1.5 }}
      >
        <SectionTitle>Conseils du moteur</SectionTitle>
        <Button
          startIcon={<AutoAwesomeRoundedIcon />}
          onClick={runEngine}
          disabled={running}
          sx={{
            color: PERF_ACCENT,
            textTransform: 'none',
            fontWeight: 600,
          }}
        >
          {running ? 'Analyse…' : "Lancer l'analyse"}
        </Button>
      </Stack>
      {advices.length === 0 ? (
        <Box sx={cardSx}>
          <Typography sx={{ color: 'rgba(255,255,255,0.55)' }}>
            Aucun conseil actif. Lancez l'analyse après quelques séances pour que
            le moteur détecte surcharge, fatigue ou irrégularité.
          </Typography>
        </Box>
      ) : (
        <Stack spacing={1.5}>
          {advices.map((a) => (
            <Box
              key={a.id}
              sx={{
                ...cardSx,
                borderLeft: `4px solid ${SEVERITY_COLOR[a.severity] || PERF_ACCENT}`,
              }}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="flex-start"
              >
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ color: '#fff', fontWeight: 600 }}>
                    {a.title}
                  </Typography>
                  <Typography
                    sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, mt: 0.5 }}
                  >
                    {a.message}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  onClick={() => dismiss(a.id)}
                  sx={{ color: 'rgba(255,255,255,0.5)', textTransform: 'none' }}
                >
                  Ignorer
                </Button>
              </Stack>
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  )
}
